import React, { useState } from 'react';
import { useAuth } from '../../../../context/AuthContext';

function AssignModal({ isOpen, onClose, onSubmit, requisition, allUsers = [], currentUser, originalUser, isMasquerading, data }) {
  const [selectedUser, setSelectedUser] = useState('');
  const [loading, setLoading] = useState(false);
  const { can } = useAuth(); // Use AuthContext's can method

  // Check if current user has assign permission using AuthContext
  // Also allow PM (role 4) and PM delegates to assign
  const hasAssignPermission = () => {
    // Check standard permission
    if (can('Assign Requisition')) {
      return true;
    }
    
    // Check if user is PM (role 4)
    const userRole = currentUser?.role || currentUser?.user_role || currentUser?.role_id;
    if (userRole == 4) {
      return true;
    }
    
    // Check if user is PM delegate
    // pm_effective_user_id contains the delegate user ID if PM has delegated
    const currentUserId = Number(currentUser?.user_id || currentUser?.id || 0);
    const pmEffective = Number(data?.pm_effective_user_id || 0);
    if (pmEffective > 0 && currentUserId === pmEffective) {
      return true;
    }
    
    return false;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedUser) return;

    // Double-check permission before submitting
    if (!hasAssignPermission()) {
      alert('You do not have permission to assign requisitions.');
      return;
    }

    setLoading(true);
    try {
      await onSubmit({
        assignToUserId: selectedUser
      });
      setSelectedUser('');
      onClose();
    } catch (error) {
      console.error('Error assigning requisition:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  // If user doesn't have permission, show error message instead of form
  if (!hasAssignPermission()) {
    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
        <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
          <div className="flex justify-between items-center pb-3">
            <h3 className="text-lg font-bold text-gray-900">Access Denied</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <span className="sr-only">Close</span>
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-red-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <div>
                <h4 className="text-sm font-medium text-red-800">Permission Required</h4>
                <p className="text-sm text-red-700 mt-1">
                  You do not have the "Assign Requisition" permission required to assign requisitions.
                  {isMasquerading && (
                    <span className="block mt-1 font-medium">
                      Note: You are currently masquerading as {currentUser.user_full_name || currentUser.user_name || 'Unknown User'} who does not have this permission.
                    </span>
                  )}
                </p>
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
        <div className="flex justify-between items-center pb-3">
          <h3 className="text-lg font-bold text-gray-900">Assign Requisition</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <span className="sr-only">Close</span>
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Show masquerading context if applicable */}
        {isMasquerading && currentUser && originalUser && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
            <div className="flex items-center">
              <svg className="w-4 h-4 text-blue-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <div className="text-sm">
                <span className="font-medium text-blue-800">Assigning as: </span>
                <span className="text-blue-700">
                  User ID: {currentUser.user_id} ({currentUser.user_full_name || currentUser.user_name || currentUser.name || 'Name not available'})
                </span>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Buyer
            </label>
            <select
              value={selectedUser}
              onChange={(e) => setSelectedUser(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="">Choose a buyer...</option>
              {(() => {
                // Handle buyers array directly since we're now passing data.buyers
                const users = Array.isArray(allUsers) ? allUsers : [];
                
                return users.map((user) => (
                  <option key={user.user_id} value={user.user_id}>
                    {user.user_full_name}
                  </option>
                ));
              })()}
            </select>
          </div>

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !selectedUser}
              className={`px-4 py-2 rounded-md disabled:cursor-not-allowed flex items-center justify-center ${
                loading 
                  ? 'bg-blue-400 text-white cursor-wait' 
                  : 'bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50'
              }`}
            >
              {loading && (
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              )}
              {loading ? 'Assigning...' : 'Assign'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AssignModal;
